import { LitElement, html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { guardedCustomElement } from '../utils/guarded-custom-element';
import { styles } from './sds-chip.styles';
import { SDS_SIZES } from '../types';
import { SDSDelegateInteractionMixin, SDSEventDispatcherMixin } from '../mixins';

import '../sds-icon/sds-icon';

type ChipVariant = 'selectable' | 'removable';
type ChipSize = 'large' | 'medium' | 'small' | Extract<SDS_SIZES, 'lg' | 'md' | 'sm'>;

export interface SDSChipProps {
	variant?: ChipVariant;
	size?: ChipSize;
	checked?: boolean;
	name?: string;
	value?: string;
	disabled?: boolean;
	readonly?: boolean;
}

@guardedCustomElement('sds-chip')
export class SDSChip extends SDSDelegateInteractionMixin(SDSEventDispatcherMixin(LitElement)) {
	// Styling
	static override styles = [styles];

	static formAssociated = true;
	public readonly internals: ElementInternals;

	static override shadowRootOptions: ShadowRootInit = {
		...LitElement.shadowRootOptions,
		delegatesFocus: true,
	};

	private _initialChecked: boolean = false;
	private _readOnly: boolean = false;

	@query('#control') control!: HTMLInputElement;

	// Reactive Props
	@property({ type: String, reflect: true }) variant: SDSChipProps['variant'] = 'selectable';
	@property({ type: String, reflect: true }) size: SDSChipProps['size'] = 'md';
	@property({ type: Boolean, reflect: true }) checked: SDSChipProps['checked'] = false;
	@property({ type: String, reflect: true }) name?: SDSChipProps['name'];
	@property({ type: String }) value?: SDSChipProps['value'];
	@property({ type: Boolean, reflect: true }) disabled: SDSChipProps['disabled'] = false;
	@property({ type: Boolean, attribute: 'readonly', reflect: true })
	// TODO: Revert til normal prop and remove readonly fallback handling in next major version of web components package.
	get readOnly(): boolean {
		return this._readOnly;
	}
	set readOnly(value: SDSChipProps['readonly']) {
		const oldVal = this._readOnly;
		this._readOnly = value ?? false;
		this.requestUpdate('readOnly', oldVal);
	}

	/** @deprecated Use readOnly instead */
	set readonly(value: SDSChipProps['readonly']) {
		this.readOnly = value;
	}

	constructor() {
		super();
		this.internals = this.attachInternals();
	}

	override connectedCallback() {
		super.connectedCallback();
		this._initialChecked = this.checked ?? false;
		this.internals.role = 'checkbox';
		if (this.checked) {
			this.internals.setFormValue(this.value ?? null);
		}
	}

	formResetCallback() {
		this.checked = this._initialChecked;
		this.internals.setFormValue(this.value ?? null);
	}

	override render() {
		const isRemovableVariant = this.variant === 'removable' && !this.readOnly && !this.disabled;

		const chipClasses = {
			'sds-chip': true,
			'sds-chip--small': this.size === 'small' || this.size === 'sm',
			'sds-chip--large': this.size === 'large' || this.size === 'lg',
			'sds-chip--removable': isRemovableVariant,
		};

		if (isRemovableVariant) {
			return html`<button class="${classMap(chipClasses)}" @click=${this._remove} @keyup="${this._keypress}">
				<slot></slot>
				<sds-icon name="close-material-outlined" size="s"></sds-icon>
			</button>`;
		}

		return html`<label class="${classMap(chipClasses)}" @click="${this._toggle}" @keyup="${this._keypress}">
			<input
				id="control"
				type="checkbox"
				name="${ifDefined(this.name)}"
				value="${ifDefined(this.value)}"
				.checked="${this.checked}"
				?checked="${this.checked}"
				?disabled="${this.disabled}"
				?readonly="${this.readOnly}"
			/>
			<slot></slot>
		</label>`;
	}

	private readonly _toggle = (event: Event) => {
		event.preventDefault();
		event.stopImmediatePropagation();

		if (this.disabled || this.readOnly) return;
		this.checked = !this.checked;
		this.internals.setFormValue(this.checked ? this.value ?? 'on' : null);
		this.dispatchSheriffEvent('change');
	};

	private readonly _remove = () => {
		if (this.disabled || this.readOnly) return;
		this.dispatchSheriffEvent('sds-remove');
	};

	private readonly _keypress = (event: KeyboardEvent) => {
		if (![' ', 'Enter'].includes(event.key)) {
			return;
		}

		if (this.variant === 'removable') {
			this._remove();
		} else {
			this._toggle(event);
		}
	};
}

declare global {
	interface HTMLElementTagNameMap {
		'sds-chip': SDSChip;
	}
}
